// 法人一覧
import React, { useState, useEffect } from 'react';
import { render } from 'react-dom';
import Moment from 'moment';
import axios from '../../../axios';
import { handleBrowserBack } from '../../../BrowserBack';
import { corporate_columns } from '../../../TableColumns';
import { Button, Col, Row } from 'react-bootstrap';
import ReactDatePicker from 'react-datepicker';
import ja from 'date-fns/locale/ja';
import MaintenanceTable from '../../../organisms/MaintenanceTable';
import LoadingModal from '../../../molecules/LoadingModal';
import { KeyValue } from '../../../types/KeyValue';
import { KWMNTGM05010A02 } from '../../../const/api_modules';
import { CorporationInfoFormatValue } from '../../../Common';
import { ItemKey } from '../../../const/Corporate';
import { convertData } from '../../../Convert';
import { convert_types } from '../../../Const';

interface User {
  ja_code: string;
  bank_code: string;
}

interface CorporateDatas {
  id: number | null;
  corporate_name: JSX.Element;
  address: JSX.Element;
  corporate_status: number;
  start_date: string;
  end_date: string;
  group_code: number;
  bank_branch_code: number;
}

interface SearchParams {
  id: number | null;
  corporate_name: string;
  address: string;
  corporate_status: string;
  start_date: Date | null;
  end_date: Date | null;
  group_code: number;
  bank_branch_code: number;
}

interface ErrorText {
  jacode?: string;
  target_date?: string;
}

const initialState = {
  id: null,
  corporate_name: '',
  address: '',
  corporate_status: '',
  start_date: null,
  end_date: null,
  group_code: 0,
  bank_branch_code: 0,
};

const CorporateList = () => {
  const [user, setUser] = useState<User>();
  const [datas, setDatas] = useState<CorporateDatas[]>([]);
  // const [search_params, setSearchParams] = useState<SearchParams[]>([]);
  const [search_params, setSearchParams] = useState<SearchParams>(initialState);
  const [group_code_list, setGroupCodeList] = useState([]);
  const [bank_branch_code_list, setBankBranchCodeList] = useState([]);
  const [corporate_status, setCorporateStatus] = useState([]);
  // ローディング画面表示状態
  const [is_loading, setIsLoading] = useState(false);

  // グローバルナビゲーション表示用
  const [role, setRole] = useState('');
  const [has_loan_aproval_system, setHasLoanAprovalSystem] = useState(false);

  // 組織区分コードの取得
  const node = document.getElementById('corporate-list');
  const user_organization = node?.dataset.organization_type as string;

  useEffect(() => {
    // ブラウザの戻る・進む禁止
    handleBrowserBack();

    getInitialState();
    // グローバルナビゲーション表示用
    setUserRole();
  }, []);

  // 初期値、セレクトボックス値を取得
  const getInitialState = async () => {
    try {
      setIsLoading(true);
      const response = await axios.get(
        `${location.origin}${process.env.MIX_MOCK_URL}/api/get_related_to_ja`
      );
      setCorporateStatus(response.data.detail.corporate_status);
      setGroupCodeList(response.data.detail.group_code_list);
      setBankBranchCodeList(response.data.detail.bank_branch_code_list);
      setUser({
        ja_code: response.data.detail.user_ja,
        bank_code: response.data.detail.user_bank,
      });
      setSearchParams(prev => ({
        ...prev,
        group_code: response.data.detail.user_ja,
        bank_branch_code: response.data.detail.user_bank,
        corporate_status: response.data.detail.corporate_status[0].key,
      }));
    } catch (error) {
      console.error(error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleInput = (event: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    return setSearchParams({ ...search_params, [event.target.id]: event.target.value });
  };

  const handleInputNumber = (event: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    return setSearchParams({ ...search_params, [event.target.id]: event.target.value });
  };

  const handleInputDate = (column: string, date: Date | [Date, Date] | null) => {
    return setSearchParams({ ...search_params, [column]: date });
  };

  /**
   * フォーカスアウト時に全角数値を半角数値に変換する 数値以外の文字は除去する
   * @param React.FocusEvent<HTMLInputElement> event フォーカスが外れた際に発生するフォーカスイベント
   * @return void
   */
  const handleBlur = (event: React.FocusEvent<HTMLInputElement>) => {
    const convert_value = convertData(convert_types.change_numeric, event.target.value);
    setSearchParams(prev => {
      return { ...prev, [event.target.id]: convert_value };
    });
  };

  // 検索処理を行う
  const executeSearch = async () => {
    try {
      setDatas([]);
      setIsLoading(true);

      const response = await axios.post(
        `${location.origin}${process.env.MIX_MOCK_URL}/api/common`,
        {
          reception_id: '',
          reception_cd: null,
          module_name: KWMNTGM05010A02,
          detail: [
            { id: 0, name: 'id', value: search_params.id },
            { id: 0, name: 'corporate_name', value: search_params.corporate_name },
            { id: 0, name: 'address', value: search_params.address },
            {
              id: 0,
              name: 'corporate_status',
              value:
                Number(search_params.corporate_status) !== 0 ? search_params.corporate_status : '',
            },
            {
              id: 0,
              name: 'start_date',
              value: search_params.start_date
                ? Moment(search_params.start_date).format('YYYY-MM-DD')
                : '',
            },
            {
              id: 0,
              name: 'end_date',
              value: search_params.end_date
                ? Moment(search_params.end_date).format('YYYY-MM-DD')
                : '',
            },
            { id: 0, name: 'group_code', value: search_params.group_code },
            {
              id: 0,
              name: 'bank_branch_code',
              value: search_params.bank_branch_code !== 0 ? search_params.bank_branch_code : '',
            },
          ],
        }
      );

      Object.values(response.data.detail).map((value: any) => {
        setDatas(prev => [
          ...prev,
          {
            id: value.id,
            corporate_name: <span title={value.corporate_name}>{value.corporate_name}</span>,
            address: <span title={value.address}>{value.address}</span>,
            corporate_status: value.corporate_status,
            start_date: CorporationInfoFormatValue(value.start_date, ItemKey.start_date),
            end_date: CorporationInfoFormatValue(value.end_date, ItemKey.end_date),
            group_code: group_code_list[value.group_code],
            bank_branch_code: bank_branch_code_list[value.bank_branch_code],
            bank_branch_name: value.bank_branch_name,
            button: (
              <Button
                variant="outline-success"
                href={`${location.origin}${process.env.MIX_MOCK_URL}/staff/corporate_info_detail?corporate_id=${value.id}`}
                className="btn-text-color"
              >
                詳細
              </Button>
            ),
          },
        ]);
      });
    } catch (error) {
      console.error(error);
    } finally {
      setIsLoading(false);
    }
  };

  // ロールを設定する
  const setUserRole = () => {
    if (node === null) {
      // nodeが正しく取得できない場合は空文字とする
      setRole('');
      setHasLoanAprovalSystem(false);
      return;
    }

    // ロール名をセット
    setRole(node.dataset.role as string);

    // 融資稟議システム導入済みフラグをセット
    if ((node.dataset.has_loan_aproval_system as string) === '1') {
      setHasLoanAprovalSystem(true);
    } else {
      setHasLoanAprovalSystem(false);
    }
  };

  return (
    <div className="corporate">
      <div className="title">法人情報</div>
      <div className="container">
        <div className="contents">
          <div className="search_wrapper">
            <Row className="my-2">
              <Col md={4}>
                <span>法人ID</span>
                <input
                  value={search_params.id || ''}
                  id="id"
                  className="form-control"
                  type="text"
                  onChange={handleInput}
                  onBlur={handleBlur}
                />
              </Col>
              <Col md={4}>
                <span>法人名</span>
                <input
                  id="corporate_name"
                  className="form-control"
                  type="text"
                  onChange={handleInput}
                />
              </Col>
            </Row>
            <Row className="my-2">
              <Col>
                <span>住所</span>
                <input id="address" className="form-control" type="text" onChange={handleInput} />
              </Col>
            </Row>
            <Row className="my-2">
              <Col md={4}>
                <span>ステータス</span>
                <select
                  id="corporate_status"
                  className="form-control"
                  onChange={handleInputNumber}
                  value={search_params.corporate_status}
                >
                  <option value=""></option>
                  {corporate_status.map((item: KeyValue) => {
                    return (
                      <option value={item.key} key={item.key}>
                        {item.value}
                      </option>
                    );
                  })}
                </select>
              </Col>
              <Col md={4}>
                <span>適用開始日</span>
                <ReactDatePicker
                  id="start_date"
                  className="form-control"
                  selected={search_params.start_date}
                  onChange={value => handleInputDate('start_date', value)}
                  locale={ja}
                  dateFormat="yyyy-MM-dd"
                  autoComplete="off"
                />
              </Col>
              <Col md={4}>
                <span>適用終了日</span>
                <ReactDatePicker
                  id="end_date"
                  className="form-control"
                  selected={search_params.end_date}
                  onChange={value => handleInputDate('end_date', value)}
                  locale={ja}
                  dateFormat="yyyy-MM-dd"
                  autoComplete="off"
                />
              </Col>
            </Row>
            <Row className="my-2">
              <Col md={4}>
                <span>JA名</span>
                <select
                  id="group_code"
                  className="form-control"
                  onChange={handleInputNumber}
                  defaultValue={user?.ja_code}
                  value={search_params.group_code}
                >
                  {Object.entries(group_code_list).map((value, index) => {
                    return (
                      <option value={value[0]} key={index}>
                        {value[1]}
                      </option>
                    );
                  })}
                </select>
              </Col>
              <Col md={4}>
                <span>取引店</span>
                <select
                  id="bank_branch_code"
                  className="form-control"
                  onChange={handleInputNumber}
                  defaultValue={user?.bank_code}
                  value={search_params.bank_branch_code}
                >
                  <option value=""></option>
                  {Object.entries(bank_branch_code_list).map((value, index) => {
                    return (
                      <option value={value[0]} key={index}>
                        {value[1]}
                      </option>
                    );
                  })}
                </select>
              </Col>
            </Row>
            <Row className="my-2">
              <Col md={8} />
              <Col md={4}>
                <div className="btn-success btn-mini d-inline-block" onClick={executeSearch}>
                  検索
                </div>
                <div className="btn-success btn-mini d-inline-block ml-2">
                  <a
                    className="text-white text-decoration-none"
                    href={`${location.origin}${process.env.MIX_MOCK_URL}/staff/corporate_entry`}
                  >
                    新規作成
                  </a>
                </div>
              </Col>
            </Row>
          </div>
        </div>
        <MaintenanceTable
          datas={datas}
          columns={corporate_columns}
          params={{
            entries: 10,
            displayEntries: true,
            scrollx: true,
            entriesOptions: [10, 50, 100],
          }}
        />
        <div className="footer-btn mb-3">
          <a
            className="btn btn-mini btn-outline-success btn-text-color"
            href={`${location.origin}${process.env.MIX_MOCK_URL}/staff/menu`}
          >
            戻る
          </a>
        </div>
        <LoadingModal show={is_loading} />
      </div>
    </div>
  );
};

if (document.getElementById('corporate-list')) {
  render(<CorporateList />, document.getElementById('corporate-list'));
}
